import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaEnvelope, FaSpinner, FaSync, FaPaperPlane, FaCheckCircle,
    FaExclamationTriangle, FaUser, FaTag, FaClock, FaInfoCircle,
} from 'react-icons/fa';

const DRAFTS_ENDPOINT = 'https://api.peaknet.com.au/signup/drafts';

const callApi = async (body) => {
    const r = await fetch(DRAFTS_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`,
        },
        body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-AU', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            timeZone: 'Australia/Sydney',
        });
    } catch { return iso; }
};

const idleTone = (hours) => {
    if (hours == null) return 'bg-[#3c3836] text-[#bdae93] border border-[#504945]';
    if (hours < 24) return 'bg-[#3c3836] text-[#fabd2f] border border-[#d79921]';
    if (hours < 72) return 'bg-[#3c3836] text-[#fe8019] border border-[#d65d0e]';
    return 'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]';
};

const formatIdle = (hours) => {
    if (hours == null) return '—';
    if (hours < 1) return 'just now';
    if (hours < 48) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
};

/**
 * Unfinished signups — people who started an application, asked us to email
 * them a link to finish, and then stopped.
 *
 * Sending is deliberately manual: a person decides who to contact and when,
 * rather than a schedule doing it. Everyone listed here ticked the resume
 * opt-in during signup, so contacting them is consented — and the backend
 * refuses to send to a record without that consent regardless of what this
 * screen asks for.
 */
const UnfinishedSignups = () => {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sendingId, setSendingId] = useState(null);
    const [sentIds, setSentIds] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await callApi({ action: 'listDrafts' });
            setDrafts(Array.isArray(data.drafts) ? data.drafts : []);
        } catch (err) {
            setError(err.message || 'Could not load unfinished signups');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sendCompletion = async (draft) => {
        const ok = window.confirm(
            `Send a "finish your application" email to ${draft.email}?\n\n` +
            `It includes a resume link and an unsubscribe link.`
        );
        if (!ok) return;

        setSendingId(draft.draftId);
        setError(null);
        try {
            await callApi({ action: 'sendCompletion', draftId: draft.draftId });
            setSentIds((prev) => ({ ...prev, [draft.draftId]: true }));
            await load();
        } catch (err) {
            setError(`Could not send to ${draft.email}: ${err.message}`);
        } finally {
            setSendingId(null);
        }
    };

    const stale = useMemo(
        () => drafts.filter((d) => (d.hoursSince ?? 0) >= 1).length,
        [drafts],
    );

    return (
        <div className="p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-xl font-bold text-[#ebdbb2] flex items-center gap-2">
                        <FaEnvelope className="text-[#fabd2f]" />
                        Unfinished signups
                    </h2>
                    <p className="text-sm text-[#bdae93] mt-1">
                        {drafts.length} saved · {stale} idle for an hour or more
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3c3836] text-[#ebdbb2] border border-[#504945] hover:bg-[#504945] disabled:opacity-50 transition-colors"
                >
                    {loading ? <FaSpinner className="animate-spin" /> : <FaSync />}
                    Refresh
                </button>
            </div>

            <div className="mb-5 flex items-start gap-2 rounded-lg border border-[#458588] bg-[#3c3836] p-3 text-sm text-[#83a598]">
                <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                <p>
                    Everyone here ticked <strong>“email me a link so I can finish later”</strong> during
                    signup, so they have consented to be contacted. Every email sent from this screen
                    identifies Peak Net Pty Ltd and carries a working unsubscribe link.
                </p>
            </div>

            {error && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#cc241d] bg-[#3c3836] p-3 text-sm text-[#fb4934]">
                    <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {loading && drafts.length === 0 && (
                <div className="flex items-center gap-3 text-[#bdae93] py-10 justify-center">
                    <FaSpinner className="animate-spin" /> Loading…
                </div>
            )}

            {!loading && drafts.length === 0 && !error && (
                <div className="text-center py-12 text-[#bdae93]">
                    <FaCheckCircle className="mx-auto text-3xl text-[#b8bb26] mb-3" />
                    <p>No unfinished signups right now.</p>
                </div>
            )}

            {drafts.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-[#504945]">
                    <table className="w-full text-sm">
                        <thead className="bg-[#3c3836] text-[#bdae93] uppercase text-xs tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Customer</th>
                                <th className="text-left px-4 py-3">Plan</th>
                                <th className="text-left px-4 py-3">Reached</th>
                                <th className="text-left px-4 py-3">Idle</th>
                                <th className="text-left px-4 py-3">Emails sent</th>
                                <th className="text-right px-4 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#504945] text-[#ebdbb2]">
                            {drafts.map((d) => (
                                <tr key={d.draftId} className="hover:bg-[#32302f]">
                                    <td className="px-4 py-3">
                                        <div className="font-medium flex items-center gap-2">
                                            <FaUser className="text-[#83a598] text-xs" />
                                            {d.data?.firstName || '—'} {d.data?.lastName || ''}
                                        </div>
                                        <div className="text-[#bdae93] text-xs mt-0.5 flex items-center gap-1.5">
                                            {d.email}
                                            {d.grantVerified === false && (
                                                <span
                                                    title="This draft was created without a verified email-check step. It may not be a genuine applicant — confirm before emailing."
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#3c3836] text-[#fb4934] border border-[#cc241d]"
                                                >
                                                    <FaExclamationTriangle className="text-[9px]" /> unverified
                                                </span>
                                            )}
                                        </div>
                                        {d.data?.phone && (
                                            <div className="text-[#bdae93] text-xs">{d.data.phone}</div>
                                        )}
                                        {d.data?.address && (
                                            <div className="text-[#928374] text-xs mt-0.5">{d.data.address}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1.5 text-[#ebdbb2]">
                                            <FaTag className="text-[#b8bb26] text-xs" />
                                            {d.data?.plan || '—'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-[#bdae93]">Step {d.step || 1}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${idleTone(d.hoursSince)}`}>
                                            <FaClock className="text-[10px]" />
                                            {formatIdle(d.hoursSince)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-[#bdae93]">
                                        {d.remindersSent || 0}
                                        {d.lastNudgeAt && (
                                            <div className="text-xs text-[#928374]">{formatDate(d.lastNudgeAt)}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => sendCompletion(d)}
                                            disabled={sendingId === d.draftId}
                                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#3c3836] text-[#fabd2f] border border-[#d79921] hover:bg-[#504945] disabled:opacity-50 transition-colors"
                                        >
                                            {sendingId === d.draftId
                                                ? <FaSpinner className="animate-spin" />
                                                : sentIds[d.draftId]
                                                    ? <FaCheckCircle className="text-[#b8bb26]" />
                                                    : <FaPaperPlane />}
                                            {sentIds[d.draftId] ? 'Sent' : 'Send link'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UnfinishedSignups;
